<script module lang="ts">
  // Login screen. Two panes on a wide window — the form and the product panel — collapsing to
  // the form alone below 900px, which is the only thing a phone needs.
  //
  // Ported from the supplied TIK design (Fayj login). Two deliberate deviations, both required
  // here: the fonts are the self-hosted IBM Plex Arabic the app already ships (the design's
  // stylesheet pulls them from Google Fonts, which this app's CSP forbids), and the figures in
  // the product panel are ones this service can stand behind — an unmeasured uptime number is
  // not something to print on a login page.
  import { ApiError } from '$lib/api/client';
  import type { I18nKey } from '$lib/i18n';

  /** `429 → rateLimited`, `0 → netError`, `too_many_connections`, other ApiError → badLogin. */
  export function loginErrorKey(err: unknown): I18nKey {
    if (err instanceof ApiError) {
      if (err.status === 429) return 'rateLimited';
      if (err.status === 0) return 'netError';
      if (err.code === 'too_many_connections') return 'tooManyConnections';
      return 'badLogin';
    }
    return 'netError';
  }
</script>

<script lang="ts">
  import { api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';
  import { tenant } from '$lib/store/tenant.svelte';
  import type { RecoveryInfo } from '$lib/api/types';

  let email = $state('');
  let password = $state('');
  let reveal = $state(false);
  let remember = $state(true);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let emailEl = $state<HTMLInputElement | null>(null);
  // Second factor: set once the password step answers `needs2fa`. The password is dropped from
  // memory at that point — only the short-lived pending id is needed from here on.
  let pending = $state<string | null>(null);
  let code = $state('');
  let codeEl = $state<HTMLInputElement | null>(null);
  // "Forgot your password" opens contacts, never a self-service reset (see routes/recovery.js).
  let helpOpen = $state(false);
  let help = $state<RecoveryInfo | null>(null);

  const domainChip = $derived(tenant.info.domains[0] ? `@${tenant.info.domains[0]}` : tenant.info.host);

  async function submit(ev: SubmitEvent) {
    ev.preventDefault();
    if (busy) return;
    busy = true;
    error = null;
    try {
      const next = await store.login(email.trim(), password, remember);
      if (next) {
        pending = next;
        password = '';
      }
    } catch (err) {
      error = t(loginErrorKey(err));
    } finally {
      busy = false;
    }
  }

  async function submitCode(ev: SubmitEvent) {
    ev.preventDefault();
    if (busy || !pending) return;
    busy = true;
    error = null;
    try {
      await store.login2fa(pending, code);
    } catch (err) {
      // The pending id is destroyed after five wrong codes and expires after five minutes;
      // either way the only way forward is the password screen again.
      if (err instanceof ApiError && err.code === 'pending_expired') {
        pending = null;
        code = '';
        error = t('twoFactorExpired');
      } else {
        error = t(err instanceof ApiError && err.code === 'bad_code' ? 'twoFactorBadCode' : loginErrorKey(err));
      }
    } finally {
      busy = false;
    }
  }

  function backToPassword(): void {
    pending = null;
    code = '';
    error = null;
  }

  async function openHelp(): Promise<void> {
    helpOpen = true;
    if (help) return;
    try {
      help = await api.recovery();
    } catch {
      help = { selfService: false, adminEmail: null, supportEmail: null, supportUrl: null, domains: [] };
    }
  }

  $effect(() => {
    if (pending) codeEl?.focus();
    else emailEl?.focus();
  });
</script>

<div class="page" dir="rtl">
  <section class="formPane">
    <div class="form">
      <header class="brand">
        <img class="wordmark" src="/img/logo-tik-ar.png" alt="تك لتقنية المعلومات" width="800" height="322" />
        <div class="titles">
          <span class="eyebrow" dir="ltr">FAYJ / WEBMAIL</span>
          <h1>{pending ? t('twoFactor') : t('loginHeading')}</h1>
          <p class="lede">{pending ? t('twoFactorPrompt') : t('loginLede')}</p>
        </div>
        <div class="chip">
          <span class="dot" aria-hidden="true"></span>
          <span dir="ltr">{domainChip}</span>
        </div>
      </header>

      {#if pending}
        <form onsubmit={submitCode}>
          <div class="field">
            <label for="fayj-code">{t('twoFactorCode')}</label>
            <input
              id="fayj-code" type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code"
              spellcheck="false" required maxlength="20" bind:value={code} bind:this={codeEl} />
          </div>
          {#if error}<p class="err" role="alert">{error}</p>{/if}
          <button type="submit" class="primary" disabled={busy}>{busy ? t('signingIn') : t('twoFactorVerify')}</button>
          <button type="button" class="ghost" onclick={backToPassword}>{t('twoFactorBack')}</button>
        </form>
      {:else}
        <form onsubmit={submit}>
          <div class="field">
            <label for="fayj-email">{t('email')}</label>
            <input
              id="fayj-email" type="email" dir="ltr" autocomplete="username" spellcheck="false"
              required placeholder={tenant.loginHint} bind:value={email} bind:this={emailEl} />
          </div>

          <div class="field">
            <div class="labelRow">
              <label for="fayj-pass">{t('password')}</label>
              <button type="button" class="link" onclick={openHelp}>{t('forgotPassword')}</button>
            </div>
            <div class="passWrap">
              <input
                id="fayj-pass" type={reveal ? 'text' : 'password'} dir="ltr"
                autocomplete="current-password" required placeholder="••••••••" bind:value={password} />
              <button type="button" class="reveal" onclick={() => (reveal = !reveal)}>
                {reveal ? t('hide') : t('show')}
              </button>
            </div>
          </div>

          {#if error}<p class="err" role="alert">{error}</p>{/if}

          <label class="remember">
            <input type="checkbox" bind:checked={remember} />
            <span>{t('keepSignedIn')}</span>
          </label>

          <button type="submit" class="primary" disabled={busy}>{busy ? t('signingIn') : t('signIn')}</button>
        </form>
      {/if}

      {#if helpOpen}
        <div class="help" role="region" aria-label={t('forgotPassword')}>
          <p>{t('recoveryNoSelfService')}</p>
          <ul>
            <li>{t('recoveryAskAdmin')}</li>
            {#if help?.adminEmail}
              <li><a href="mailto:{help.adminEmail}" dir="ltr">{help.adminEmail}</a></li>
            {/if}
            <li>{t('recoveryAdminOwn')}</li>
          </ul>
          {#if help?.supportEmail || help?.supportUrl}
            <p class="muted">
              {t('recoverySupport')}
              {#if help.supportEmail}<a href="mailto:{help.supportEmail}" dir="ltr">{help.supportEmail}</a>{/if}
              {#if help.supportUrl}<a href={help.supportUrl} dir="ltr">{help.supportUrl}</a>{/if}
            </p>
          {/if}
          <button type="button" class="ghost" onclick={() => (helpOpen = false)}>{t('close')}</button>
        </div>
      {:else}
        <footer class="foot">
          <p class="muted">{t('loginHelpHint')}</p>
          <div class="meta">
            <span dir="ltr">TLS 1.3 · IMAP · SMTP</span>
            <span dir="ltr">AR | EN</span>
          </div>
        </footer>
      {/if}
    </div>
  </section>

  <aside class="pitchPane" aria-hidden="true">
    <div class="grid"></div>
    <div class="pitchTop">
      <img src="/img/logo.svg" alt="" width="28" height="28" />
      <span class="pitchName">{tenant.name ?? t('appName')}</span>
      <span class="by" dir="ltr">by TIK</span>
    </div>

    <div class="pitchBody">
      <span class="rule"></span>
      <h2>{t('pitchTitle')}<br /><span class="light">{t('pitchTitleSoft')}</span></h2>
      <p>{t('pitchLede')}</p>

      <ul class="points">
        <li>
          <span class="ic blue">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></svg>
          </span>
          <div><strong>{t('pitchPoint1')}</strong><span>{t('pitchPoint1Sub')}</span></div>
        </li>
        <li>
          <span class="ic dark">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          </span>
          <div><strong>{t('pitchPoint2')}</strong><span>{t('pitchPoint2Sub')}</span></div>
        </li>
        <li>
          <span class="ic gray">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></svg>
          </span>
          <div><strong>{t('pitchPoint3')}</strong><span>{t('pitchPoint3Sub')}</span></div>
        </li>
      </ul>
    </div>

    <div class="stats">
      <div><span class="num" dir="ltr">IMAP · SMTP</span><span class="cap">{t('pitchStat1')}</span></div>
      <div><span class="num">{t('pitchStat2Num')}</span><span class="cap">{t('pitchStat2')}</span></div>
    </div>
  </aside>
</div>

<style>
  /* @css auth/Login start */
  .page {
    min-height: 100%;
    display: grid;
    grid-template-columns: 1fr;
    font-family: var(--font-app);
    color: var(--text-body);
    background: var(--surface-page, var(--neutral-0));
  }
  /* Two panes only when there is room for both: the form keeps its 400px measure and the
     product panel needs about as much again before it stops being decoration. */
  @media (min-width: 900px) {
    .page {
      grid-template-columns: minmax(420px, 1fr) minmax(420px, 1fr);
    }
  }

  .formPane {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 40px;
  }
  .form {
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .brand {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .wordmark {
    height: 34px;
    width: auto;
    align-self: flex-start;
  }
  /* The supplied wordmark is black type with a coloured dot grid; on the dark theme it would be
     black on near-black. Inverting and rotating the hue restores legibility while keeping the
     dots recognisably coloured — a light-variant asset would be better and is worth asking for. */
  :global(html.tik-dark) .wordmark {
    filter: invert(1) hue-rotate(180deg);
  }
  .titles {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .eyebrow {
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    color: var(--blue-500);
  }
  h1 {
    margin: 0;
    font-size: 30px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--text-strong);
  }
  .lede {
    margin: 0;
    font-size: 14px;
    line-height: 1.75;
    color: var(--text-muted);
    max-width: 34ch;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    padding: 6px 10px 6px 12px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-subtle);
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-strong);
  }
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--success-500);
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .labelRow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-strong);
  }
  input[type='email'],
  input[type='password'],
  input[type='text'] {
    width: 100%;
    box-sizing: border-box;
    height: 46px;
    padding: 0 14px;
    font-family: var(--font-mono);
    font-size: 14px;
    color: var(--text-strong);
    background: var(--surface-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    outline: none;
    transition: var(--transition-control, border-color 0.15s ease, box-shadow 0.15s ease);
  }
  input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
  }
  .passWrap {
    position: relative;
    display: flex;
  }
  /* The field is always dir="ltr" (a password is not Arabic text), so the reveal button sits at
     the physical right whatever the page direction — `inset-inline-end` would put it on the left
     in RTL, straight on top of the characters being typed. */
  .passWrap input {
    padding-right: 72px;
    padding-left: 14px;
  }
  .reveal {
    position: absolute;
    right: 6px;
    top: 6px;
    height: 34px;
    padding: 0 10px;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    background: transparent;
    border: 0;
    border-radius: var(--radius-control);
    cursor: pointer;
  }
  .reveal:hover {
    background: var(--surface-sunken);
    color: var(--text-strong);
  }
  .remember {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 400;
    color: var(--text-body);
  }
  .remember input {
    width: 16px;
    height: 16px;
    accent-color: var(--blue-500);
    cursor: pointer;
  }
  .primary {
    height: 52px;
    width: 100%;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-on-accent, #fff);
    background: var(--blue-500);
    border: 0;
    border-radius: var(--radius-control);
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .primary:hover:not(:disabled) {
    background: var(--blue-600);
  }
  .primary:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .ghost,
  .link {
    background: none;
    border: 0;
    font: inherit;
    font-size: 13px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
  }
  .link {
    color: var(--text-link, var(--blue-600));
  }
  .ghost:hover,
  .link:hover {
    color: var(--text-strong);
    text-decoration: underline;
  }
  .err {
    margin: 0;
    padding: 10px 12px;
    font-size: 13px;
    color: var(--danger-500);
    background: var(--danger-100);
    border: 1px solid var(--danger-100);
    border-radius: var(--radius-control);
  }

  .help,
  .foot {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid var(--border-default);
    font-size: 13px;
    line-height: 1.75;
  }
  .help p,
  .foot p {
    margin: 0;
  }
  .help ul {
    margin: 0;
    padding-inline-start: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: var(--text-body);
  }
  .help a {
    color: var(--text-link, var(--blue-600));
  }
  .muted {
    color: var(--text-muted);
  }
  .meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-faint);
  }

  /* ── product panel ───────────────────────────────────────────────────────── */
  .pitchPane {
    position: relative;
    overflow: hidden;
    display: none;
    flex-direction: column;
    justify-content: space-between;
    padding: 64px 56px;
    background: var(--surface-subtle);
    border-inline-start: 1px solid var(--border-default);
  }
  @media (min-width: 900px) {
    .pitchPane {
      display: flex;
    }
  }
  .grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--grid-line) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(120% 90% at 70% 20%, #000 0%, transparent 75%);
    -webkit-mask-image: radial-gradient(120% 90% at 70% 20%, #000 0%, transparent 75%);
  }
  .pitchTop {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .pitchName {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-strong);
  }
  .by {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-muted);
    border-inline-start: 1px solid var(--border-default);
    padding-inline-start: 12px;
  }
  .pitchBody {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 40px;
    padding: 48px 0;
  }
  .rule {
    width: 44px;
    height: 3px;
    background: var(--blue-400);
  }
  h2 {
    margin: 0;
    font-size: clamp(28px, 3.2vw, 40px);
    font-weight: 600;
    line-height: 1.2;
    color: var(--text-strong);
    max-width: 30ch;
  }
  .light {
    font-weight: 300;
    color: var(--text-muted);
  }
  .pitchBody > p {
    margin: 0;
    font-size: 15px;
    line-height: 1.8;
    color: var(--text-body);
    max-width: 40ch;
  }
  .points {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .points li {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
  .points div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .points strong {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-strong);
  }
  .points span {
    font-size: 13px;
    line-height: 1.7;
    color: var(--text-muted);
  }
  .ic {
    flex: none;
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border-radius: 50%;
  }
  .ic.blue {
    background: var(--blue-50);
    color: var(--blue-600);
  }
  .ic.dark {
    background: var(--neutral-900);
    color: var(--neutral-0);
  }
  .ic.gray {
    background: var(--neutral-100);
    color: var(--neutral-700);
  }
  .stats {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 40px;
    padding-top: 28px;
    border-top: 1px solid var(--border-default);
    justify-content: start;
  }
  .stats div {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .num {
    font-family: var(--font-mono);
    font-size: 20px;
    font-weight: 600;
    color: var(--text-strong);
  }
  .cap {
    font-size: 12px;
    color: var(--text-muted);
  }

  /* A 768px-tall laptop is a very common window and the panel's 64px padding tipped the page
     into scrolling; trim the vertical rhythm rather than the content. */
  @media (min-width: 900px) and (max-height: 820px) {
    .formPane {
      padding-block: 40px;
    }
    .pitchPane {
      padding: 40px 48px;
    }
    .pitchBody {
      gap: 28px;
      padding: 32px 0;
    }
    .points {
      gap: 16px;
    }
  }

  /* Small screens: the form is the whole page, and it should not start halfway down it. */
  @media (max-width: 899px) {
    .formPane {
      padding: 32px 20px 40px;
      align-items: flex-start;
    }
    .form {
      gap: 24px;
      margin-inline: auto;
    }
    h1 {
      font-size: 24px;
    }
  }
  @media (max-width: 380px) {
    .formPane {
      padding: 24px 16px 32px;
    }
    .wordmark {
      height: 28px;
    }
  }
  /* Landscape phones: the viewport is short, so vertical padding has to give way first. */
  @media (max-height: 560px) and (max-width: 899px) {
    .formPane {
      padding-block: 20px;
    }
    .form {
      gap: 18px;
    }
  }
  /* @css auth/Login end */
</style>
